import React, { useState, useEffect } from 'react';
import Login from './components/Login';
import Landing from './components/Landing';
import Archive from './components/Archive';
import Favorites from './components/Favorites';
import Admin from './components/admin/Admin';
import PrivacyTerms from './components/PrivacyTerms';
import BriefingPage from './components/BriefingPage';
import ArticlePage from './components/ArticlePage';
import StoryPage from './components/StoryPage';
import { Routes, Route, useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './theme.css';
import './components.css';
import axios from 'axios';
import LogoSvg from './components/LogoSvg';

function App() {
  const [authUser, setAuthUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    // Initialize from localStorage or default to true (dark mode)
    const savedTheme = localStorage.getItem('wireTheme');
    return savedTheme === 'dark' || savedTheme === null;
  });
  const [isLoading, setIsLoading] = useState(true);






  // Check current session
  const navigate = useNavigate();
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get('/api/auth/me');
        setAuthUser(res.data || null);
      } catch (error) {
        setAuthUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
    
    // Intercept 401s
    const id = axios.interceptors.response.use(
      r => r,
      err => {
        if (err?.response?.status === 401) {
          setAuthUser(null);
          navigate('/admin');
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);

  // Apply theme to body and save to localStorage
  useEffect(() => {
    document.body.className = isDarkMode ? 'dark-theme' : 'light-theme';
    localStorage.setItem('wireTheme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  // Toggle theme function
  const toggleTheme = () => {
    setIsDarkMode(prev => !prev);
  };

  // Auth handlers
  const doLogout = async () => {
    await axios.post('/api/auth/logout');
    setAuthUser(null);
    navigate('/');
  };

  const onLoggedIn = (user) => {
    setAuthUser(user);
    navigate('/');
  };

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Theme Toggle Button - Bottom Right */}
      <button
        className="theme-toggle theme-toggle-button theme-toggle-bottom-right"
        onClick={toggleTheme}
        style={{
          backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
        }}
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        <i 
          className="fa-solid fa-lightbulb theme-toggle-icon"
          style={{ 
            color: isDarkMode ? '#ffd700' : '#333333'
          }}
        />
      </button>

      <Routes>
        <Route path="/" element={
          authUser ? 
            <Landing authUser={authUser} onLogout={doLogout} /> : 
            <Login onLoggedIn={onLoggedIn} />
        } />
        <Route path="/admin" element={
          authUser && authUser.is_admin ? 
            <Admin authUser={authUser} onLogout={doLogout} isDarkMode={isDarkMode} /> : 
            authUser ? 
              <div className="unauthorized-container">
                <h2>Access Denied</h2>
                <p>You don't have permission to access the admin panel.</p>
                <button className="btn btn-primary" onClick={() => navigate('/')}>
                  Return to Home
                </button>
              </div> :
              <Login onLoggedIn={onLoggedIn} />
        } />
        <Route path="/archive" element={
          <Archive authUser={authUser} onLogout={doLogout} />
        } />
        <Route path="/favorites" element={
          <Favorites authUser={authUser} onLogout={doLogout} />
        } />
        <Route path="/privacy-terms" element={
          <PrivacyTerms authUser={authUser} onLogout={doLogout} />
        } />
        <Route path="/briefing/:summaryId" element={
          <BriefingPage />
        } />
        <Route path="/article/:articleId" element={
          <ArticlePage />
        } />
        <Route path="/story/:articleId" element={
          <StoryPage />
        } />
      </Routes>

      {/* Footer - Now appears on all pages */}
      <footer className="section-footer">
        <div className="container">
          <div className="row align-items-center mb-4">
            <div className="col-md-10">
              <div className="d-flex align-items-center">
                <LogoSvg 
                  alt="Arkansas Electric Cooperatives" 
                  className="logo-medium"
                />
                <div>
                  <p className="footer-text">
                    ©{new Date().getFullYear()} Arkansas Electric Cooperatives • all rights reserved.
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-2 text-md-end">
              <a href="/privacy-terms" className="text-decoration-none">
                <span className="text-muted">Privacy and Terms</span>
              </a>
            </div>
          </div>
          <div className="row">
            <div className="col-12">
              <div className="copyright-notice copyright-notice-content">
                <p className="copyright-title">
                  Copyright Notice:
                </p>
                <p className="copyright-text">
                  The daily news briefs provided offer a snapshot of media coverage on relevant topics for enhanced situational awareness. The inclusion of content in the news brief does not indicate AECC or AECI's endorsement of the perspectives contained in the articles. This document may contain copyrighted material whose use has not been specifically authorized by the copyright owner. AECI is making this article available in its effort to advance the understanding of energy policy. AECI believes that this constitutes a "fair use" of the copyrighted material as provided for in Section 107 of the U.S. Copyright Law. If you wish to use this copyrighted material for purposes of your own purpose that goes beyond "fair use," you must obtain permission from the copyright owner. If your copyrighted material appears on this article and you disagree with AECI's assessment that it constitutes "fair use," please contact the Communications Team.
                </p>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default App;
